import { SUBSCRIBED_CALENDARS } from '../constants/subscribedCalendars';

const ENABLED_CALENDARS_STORAGE_KEY = 'enabled_calendars';
const ROOM_CLOSURE_NOTICE_STORAGE_KEY = 'room_closure_notice_dismissed';

/**
 * localStorage에서 활성화된 캘린더 목록을 가져옵니다.
 * 저장된 값이 없으면 기본값을 반환합니다.
 * @returns {string[]} 활성화된 캘린더 이름 배열
 */
export const loadEnabledCalendarsFromLocalStorage = (): string[] => {
  try {
    const storedValue = localStorage.getItem(ENABLED_CALENDARS_STORAGE_KEY);
    if (storedValue) {
      console.log('[UserPreference] localStorage에서 저장된 캘린더 설정을 불러왔습니다.', JSON.parse(storedValue));
      return JSON.parse(storedValue);
    }
    console.log('[UserPreference] localStorage에 저장된 설정이 없어 기본 캘린더 설정을 사용합니다.');
  } catch (error) {
    console.error('[UserPreference] localStorage에서 캘린더 설정을 불러오는 중 오류가 발생했습니다:', error);
  }
  
  // 저장된 값이 없거나 오류 발생 시 기본값 반환
  return SUBSCRIBED_CALENDARS.filter(cal => cal.enabled).map(cal => cal.name);
};

/**
 * 활성화된 캘린더 목록을 localStorage에 저장합니다.
 * @param {string[]} calendars - 활성화된 캘린더 이름 배열
 */
export const saveEnabledCalendarsToLocalStorage = (calendars: string[]): void => {
  try {
    localStorage.setItem(ENABLED_CALENDARS_STORAGE_KEY, JSON.stringify(calendars));
    console.log('[UserPreference] 활성화된 캘린더 설정을 localStorage에 저장했습니다.', calendars);
  } catch (error) {
    console.error('[UserPreference] localStorage에 캘린더 설정을 저장하는 중 오류가 발생했습니다:', error);
  }
};

/** 오늘 날짜를 'YYYY-MM-DD' 로. 접속한 기기의 날짜(국내 사용자는 한국 날짜)를 쓴다. */
const todayKey = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * 회의실 예약 기간 안내를 '오늘 하루 보지 않기'로 닫았는지 확인합니다.
 *
 * 저장값은 "안내 내용 + 닫은 날짜" 입니다. 따라서
 * - 날짜가 바뀌면 (= 다음 날) 다시 표시되고,
 * - 회의실이나 종료일이 바뀌어도 내용이 달라지므로 다시 표시됩니다.
 *
 * @param {string} signature - utils/roomAvailability 의 buildClosureNoticeSignature 결과
 */
export const isRoomClosureNoticeDismissed = (signature: string): boolean => {
  try {
    return localStorage.getItem(ROOM_CLOSURE_NOTICE_STORAGE_KEY) === `${signature}@${todayKey()}`;
  } catch (error) {
    // 사생활 보호 모드 등에서 접근이 막히면 안내를 띄우는 쪽으로 둔다
    console.error('[UserPreference] 회의실 안내 설정을 읽는 중 오류가 발생했습니다:', error);
    return false;
  }
};

/**
 * 이 탭에서 오늘 이미 안내를 띄웠는지 확인합니다.
 *
 * 로그인 화면에서 띄우고 로그인 직후 또 띄우면 몇 초 사이에 두 번 보게 됩니다.
 * 그래서 한 번 띄운 사실을 sessionStorage 에 남깁니다.
 *
 * 날짜까지 함께 저장하므로, 탭을 며칠 열어 두어도 날짜가 바뀌면 다시 보여줍니다.
 * 사용 종료가 다가오는 동안 계속 상기시키기 위함입니다.
 */
export const isRoomClosureNoticeShownToday = (signature: string): boolean => {
  try {
    return sessionStorage.getItem(ROOM_CLOSURE_NOTICE_STORAGE_KEY) === `${signature}@${todayKey()}`;
  } catch (error) {
    console.error('[UserPreference] 회의실 안내 표시 여부를 읽는 중 오류가 발생했습니다:', error);
    return false;
  }
};

/** 이 탭에서 오늘 안내를 띄웠다고 기록합니다. */
export const markRoomClosureNoticeShown = (signature: string): void => {
  try {
    sessionStorage.setItem(ROOM_CLOSURE_NOTICE_STORAGE_KEY, `${signature}@${todayKey()}`);
  } catch (error) {
    console.error('[UserPreference] 회의실 안내 표시 여부를 저장하는 중 오류가 발생했습니다:', error);
  }
};

/**
 * 회의실 예약 기간 안내를 오늘 하루 보지 않도록 저장합니다.
 * @param {string} signature - utils/roomAvailability 의 buildClosureNoticeSignature 결과
 */
export const dismissRoomClosureNotice = (signature: string): void => {
  try {
    localStorage.setItem(ROOM_CLOSURE_NOTICE_STORAGE_KEY, `${signature}@${todayKey()}`);
    console.log('[UserPreference] 회의실 예약 기간 안내를 오늘 하루 보지 않도록 저장했습니다.');
  } catch (error) {
    console.error('[UserPreference] 회의실 안내 설정을 저장하는 중 오류가 발생했습니다:', error);
  }
};
